import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatMoney, formatPercent } from "@/lib/prefs";
import { computeRecipeCost } from "@/lib/units";
import { Plus, ChefHat, GlassWater, Loader2, Utensils, Trash2 } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import ImageUpload, { resolveImageUrl } from "@/components/ImageUpload";
import { useModuleTour, TourButton } from "@/components/useModuleTour";

const emptyRecipe = { name: "", bracket: "food", servings: 1, sell_price: 0, image_url: "", description: "" };

function NewRecipeDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(emptyRecipe);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(emptyRecipe); }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/recipes", {
        ...form,
        servings: Number(form.servings) || 1,
        sell_price: Number(form.sell_price) || 0,
        ingredients: [],
        method: [],
      });
      toast.success("Recipe created");
      onCreated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="recipe-new-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">New recipe</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="recipe-new-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.bracket} onValueChange={(v) => setForm({ ...form, bracket: v })}>
                <SelectTrigger data-testid="recipe-new-bracket"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="food">Food</SelectItem>
                  <SelectItem value="drink">Drink</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Servings</Label>
              <Input type="number" min="1" value={form.servings} onChange={(e) => setForm({ ...form, servings: e.target.value })} data-testid="recipe-new-servings" />
            </div>
          </div>
          <div>
            <Label>Sell price</Label>
            <Input type="number" step="0.01" value={form.sell_price} onChange={(e) => setForm({ ...form, sell_price: e.target.value })} data-testid="recipe-new-sell" />
          </div>
          <div>
            <ImageUpload
              value={form.image_url}
              onChange={(v) => setForm({ ...form, image_url: v })}
              testId="recipe-new-image"
              label="Hero image (optional)"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.name} data-testid="recipe-new-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecipeCard({ recipe, cost, onDelete }) {
  const foodOver = cost.foodCostPercent > 35;
  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete "${recipe.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/recipes/${recipe.id}`);
      toast.success("Recipe deleted");
      onDelete();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  return (
    <Link
      to={`/recipes/${recipe.id}`}
      className="rounded-2xl glass-card overflow-hidden card-hover group relative"
      data-testid={`recipe-card-${recipe.id}`}
    >
      {/* Hover-only delete button. stopPropagation keeps the card from
          navigating into the recipe detail when the trash is clicked. */}
      <button
        onClick={handleDelete}
        className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/60 backdrop-blur-md border border-white/20 flex items-center justify-center text-white/80 hover:text-destructive hover:border-destructive/60 opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label={`Delete ${recipe.name}`}
        data-testid={`recipe-delete-${recipe.id}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
      <div className="aspect-[16/10] bg-secondary relative overflow-hidden">
        {recipe.image_url ? (
          <img
            src={resolveImageUrl(recipe.image_url)}
            alt={recipe.name}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
            style={{ transitionDuration: "300ms" }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground/40">
            {recipe.bracket === "drink" ? <GlassWater className="w-10 h-10" /> : <Utensils className="w-10 h-10" />}
          </div>
        )}
        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-card/95 label-eyebrow text-[0.55rem] capitalize">
          {recipe.bracket}
        </div>
      </div>
      <div className="p-4">
        <div className="font-display text-xl leading-tight mb-2">{recipe.name}</div>
        <div className="flex items-end justify-between">
          <div>
            <div className="label-eyebrow text-[0.55rem]">Cost / serving</div>
            <div className="font-display text-lg tabular">{formatMoney(cost.perServing)}</div>
          </div>
          <div className="text-right">
            <div className="label-eyebrow text-[0.55rem]">Food cost</div>
            <div
              className={`text-sm font-semibold tabular ${
                cost.sellPrice > 0
                  ? foodOver ? "text-destructive" : "text-[hsl(var(--success))]"
                  : "text-muted-foreground"
              }`}
            >
              {cost.sellPrice > 0 ? formatPercent(cost.foodCostPercent, 1) : "no price set"}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, i] = await Promise.all([api.get("/recipes"), api.get("/ingredients")]);
      setRecipes(r.data);
      setIngredients(i.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const byId = useMemo(() => Object.fromEntries(ingredients.map((i) => [i.id, i])), [ingredients]);

  const enrich = (r) => ({ recipe: r, cost: computeRecipeCost({ recipe: r, ingredientsById: byId }) });
  const food = recipes.filter((r) => r.bracket === "food").map(enrich);
  const drink = recipes.filter((r) => r.bracket === "drink").map(enrich);

  const tourSteps = useMemo(() => ([
    {
      selector: '[data-testid="recipes-tabs"]',
      title: "Food and drinks in one place",
      body: "Switch between the food menu and the drinks list. Every card shows live food cost and margin.",
      placement: "bottom",
    },
    {
      selector: '[data-testid="recipe-new-btn"]',
      title: "Add a new recipe",
      body: "Add ingredients, portion sizes, and a sell price. The platform computes food cost percent, plate margin, and suggested price on entry.",
      placement: "left",
    },
  ]), []);
  const tour = useModuleTour("recipes_v1", tourSteps);

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid="recipes-page">
      {tour.TourNode}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">Menu</div>
          <h1 className="font-display text-3xl md:text-4xl leading-none mt-2">Recipes</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-md">
            Food and drinks costed in real time. Click any card to audit the full breakdown.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TourButton onClick={tour.start} testId="recipes-tour-btn" />
          <Button className="gap-2" onClick={() => setDialogOpen(true)} data-testid="recipe-new-btn">
            <Plus className="w-4 h-4" /> New recipe
          </Button>
        </div>
      </header>

      <Tabs defaultValue="food" className="w-full">
        <TabsList data-testid="recipes-tabs">
          <TabsTrigger value="food" className="gap-2" data-testid="tab-food">
            <ChefHat className="w-4 h-4" /> Food • {food.length}
          </TabsTrigger>
          <TabsTrigger value="drink" className="gap-2" data-testid="tab-drink">
            <GlassWater className="w-4 h-4" /> Drinks • {drink.length}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="food" className="mt-6">
          {food.length === 0 ? (
            <EmptyState
              illustration="empty_plate"
              eyebrow="Menu"
              title="No food recipes yet."
              body="Add your first recipe to compute food cost percent, gross margin, and suggested sell price. Every ingredient, portion, and yield is reconciled on entry."
              ctaLabel="Create your first recipe"
              ctaIcon={Plus}
              onCtaClick={() => setDialogOpen(true)}
              ctaTestId="empty-recipes-food-add"
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {food.map(({ recipe, cost }) => <RecipeCard key={recipe.id} recipe={recipe} cost={cost} onDelete={load} />)}
            </div>
          )}
        </TabsContent>
        <TabsContent value="drink" className="mt-6">
          {drink.length === 0 ? (
            <EmptyState
              illustration="wine_glass"
              eyebrow="Cellar"
              title="No drinks costed yet."
              body="Log a cocktail or a wine pour to price the yield, the glass, and the labour minute against your live pantry costs."
              ctaLabel="Create your first drink"
              ctaIcon={Plus}
              onCtaClick={() => setDialogOpen(true)}
              ctaTestId="empty-recipes-drink-add"
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {drink.map(({ recipe, cost }) => <RecipeCard key={recipe.id} recipe={recipe} cost={cost} onDelete={load} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <NewRecipeDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={load} />
    </div>
  );
}
